import { GlobalSearchEntityType } from 'generated/graphql';
import type { GlobalSearchForSearchOverlayQuery } from 'generated/graphql';
import { z } from 'zod/v4';

export type SearchResultHit =
  GlobalSearchForSearchOverlayQuery['globalSearch']['results'][number];
export type UserSearchHit = Extract<
  SearchResultHit,
  { __typename?: 'UserSearchHit' }
>;
export type DeskSearchHit = Extract<
  SearchResultHit,
  { __typename?: 'DeskSearchHit' }
>;
export type SpaceSearchHit = Extract<
  SearchResultHit,
  { __typename?: 'SpaceSearchHit' }
>;
export type CustomResourceSearchHit = Extract<
  SearchResultHit,
  { __typename?: 'CustomResourceSearchHit' }
>;

/**
 * A category filter selection. Built-in types (USER/DESK/SPACE) carry only the
 * entity type; custom resource categories also carry the category (template)
 * id that goes back into GlobalSearchFilter.entityTypeFilters, plus the
 * category's display name for analytics. Identity is entityType +
 * categoryTypeId (see isSameCategorySelection); displayName is informational.
 */
export type SearchCategorySelection = {
  entityType: GlobalSearchEntityType;
  categoryTypeId?: string | null;
  displayName?: string | null;
};

export const isSameCategorySelection = (
  a: SearchCategorySelection | null,
  b: SearchCategorySelection | null
): boolean => {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.entityType === b.entityType &&
    (a.categoryTypeId ?? null) === (b.categoryTypeId ?? null)
  );
};

const searchFields = {
  id: z.string(),
  entityId: z.string(),
  displayName: z.string(),
  entityType: z.enum(GlobalSearchEntityType),
};

const DeskSearchHitSchema = z.object({
  __typename: z.literal('DeskSearchHit'),
  ...searchFields,
  locationId: z.string(),
  locationName: z.string().nullable().optional(),
  levelId: z.string().nullable().optional(),
  levelName: z.string().nullable().optional(),
});

const SpaceSearchHitSchema = z.object({
  __typename: z.literal('SpaceSearchHit'),
  ...searchFields,
  locationId: z.string(),
  locationName: z.string().nullable().optional(),
  levelId: z.string().nullable().optional(),
  levelName: z.string().nullable().optional(),
});

const UserSearchHitSchema = z.object({
  __typename: z.literal('UserSearchHit'),
  ...searchFields,
  email: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
});

const CustomResourceSearchHitSchema = z.object({
  __typename: z.literal('CustomResourceSearchHit'),
  ...searchFields,
  locationId: z.string(),
  locationName: z.string().nullable().optional(),
  levelId: z.string().nullable().optional(),
  levelName: z.string().nullable().optional(),
  categoryTypeId: z.string(),
  categoryName: z.string().nullable().optional(),
  // Optional so snapshots saved before this field existed still restore.
  resourceIcon: z.string().nullable().optional(),
});

const SearchResultHitSchema = z.discriminatedUnion('__typename', [
  DeskSearchHitSchema,
  SpaceSearchHitSchema,
  UserSearchHitSchema,
  CustomResourceSearchHitSchema,
]);

const CategorySelectionSchema = z.object({
  entityType: z.enum(GlobalSearchEntityType),
  categoryTypeId: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
});

export const SearchOverlaySnapshotSchema = z.object({
  sessionToken: z.string(),
  orgId: z.string(),
  query: z.string(),
  selectedLocationId: z.string().nullable(),
  selectedCategory: CategorySelectionSchema.nullable(),
  results: z
    .array(SearchResultHitSchema)
    .transform((hits) => hits as SearchResultHit[]),
  savedAt: z.number(),
});

export type SearchOverlaySnapshot = z.infer<typeof SearchOverlaySnapshotSchema>;
